// apps/client/src/world/terrain/field.ts — port of look-demo terrain.ts terraceShapes, generalised to any sampler and bounds (D-05)
import { tuning } from '@whiskerton/content';
import { groundAt, tierAt, type MapData } from '@whiskerton/shared';
import * as THREE from 'three';
type Tiles = Pick<MapData, 'w' | 'h' | 'tiles'>;
export const SEA_LEVEL = -0.3;
const WATER = new Set(['sea', 'river', 'pond']);

/** Bilinear over tile centres of "land at tier ≥ k". Water tiles are carved out of their own tier (a river or pond at tier t
 *  is a hole in terraces t and above, and terraces below t still run under it as its floor); the 0.5 contour is the tier's edge. */
export function tierField(map: Tiles, tier: number): (x: number, z: number) => number {
  const v = new Float32Array(map.w * map.h);
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    const t = tierAt(map, x, y);
    v[y * map.w + x] = (WATER.has(groundAt(map, x, y)) ? t > tier : t >= tier) ? 1 : 0;
  }
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= map.w || y >= map.h ? 0 : v[y * map.w + x]!);
  return (x, z) => {
    const fx = x - 0.5, fz = z - 0.5, ix = Math.floor(fx), iz = Math.floor(fz), tx = fx - ix, tz = fz - iz;
    const a = at(ix, iz), b = at(ix + 1, iz), c = at(ix, iz + 1), d = at(ix + 1, iz + 1);
    return a * (1 - tx) * (1 - tz) + b * tx * (1 - tz) + c * (1 - tx) * tz + d * tx * tz;
  };
}

/** A smoothed contour keeps its outline within this (world units) when simplified, and no straight run longer than CONTOUR_RUN: the
 *  curved-world shader bends vertices, so a long side's middle would sag between its ends (at 1 unit, by under a millimetre). */
export const CONTOUR_EPS = 0.01, CONTOUR_RUN = 1;
type XZ = [number, number];
const segDist = (p: XZ, a: XZ, b: XZ) => {
  const dx = b[0] - a[0], dz = b[1] - a[1], l2 = dx * dx + dz * dz;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / l2)) : 0;
  return Math.hypot(p[0] - a[0] - dx * t, p[1] - a[1] - dz * t);
};
/** Douglas–Peucker on a closed loop (light and look, 2026-10-16): keeps the points it takes to stay within `eps` of the outline, then
 *  splits every run longer than `maxRun` evenly. A smoothed contour had a point every ~0.12 units, straight runs included, and each
 *  point is fourteen side triangles once bevelled. */
export function simplifyLoop(pts: readonly XZ[], eps: number, maxRun: number): XZ[] {
  const n = pts.length;
  if (n < 4) return pts.map((p) => [p[0], p[1]]);
  let far = 0, best = -1;
  for (let i = 1; i < n; i++) { const d = Math.hypot(pts[i]![0] - pts[0]![0], pts[i]![1] - pts[0]![1]); if (d > best) { best = d; far = i; } }
  const keep = new Uint8Array(n);
  keep[0] = keep[far] = 1;
  const stack: [number, number][] = [[0, far], [far, n]];                 // index n is point 0 again: the loop closes
  while (stack.length) {
    const [a, b] = stack.pop()!, A = pts[a % n]!, B = pts[b % n]!;
    let at = -1, worst = eps;
    for (let i = a + 1; i < b; i++) { const d = segDist(pts[i]!, A, B); if (d > worst) { worst = d; at = i; } }
    if (at >= 0) { keep[at] = 1; stack.push([a, at], [at, b]); }
  }
  const kept = pts.filter((_, i) => keep[i]), out: XZ[] = [];
  kept.forEach((a, i) => {
    const b = kept[(i + 1) % kept.length]!, k = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / maxRun);
    out.push([a[0], a[1]]);
    for (let j = 1; j < k; j++) out.push([a[0] + ((b[0] - a[0]) * j) / k, a[1] + ((b[1] - a[1]) * j) / k]);
  });
  return out;
}

/** Marching squares at `level`, two Chaikin passes, simplified (simplifyLoop), then shapes with holes (even/odd nesting). Shape
 *  coordinates are (x, −z). */
export function contourShapes(sample: (x: number, z: number) => number, level: number, b: { x0: number; z0: number; w: number; h: number }, step: number): THREE.Shape[] {
  const nx = Math.round(b.w / step) + 1, nz = Math.round(b.h / step) + 1;
  const F = new Float32Array(nx * nz);
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++)
    F[j * nx + i] = i === 0 || j === 0 || i === nx - 1 || j === nz - 1 ? -99 : sample(b.x0 + i * step, b.z0 + j * step);
  type P = [string, number, number];
  const segs: [P, P][] = [];
  const lerp = (a: number, c: number, t: number) => a + (c - a) * t;
  for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
    const tl = F[j * nx + i]!, tr = F[j * nx + i + 1]!, br = F[(j + 1) * nx + i + 1]!, bl = F[(j + 1) * nx + i]!;
    const code = (tl >= level ? 8 : 0) | (tr >= level ? 4 : 0) | (br >= level ? 2 : 0) | (bl >= level ? 1 : 0);
    if (code === 0 || code === 15) continue;
    const px = (k: number) => b.x0 + k * step, pz = (k: number) => b.z0 + k * step;
    const T: P = [`h${i},${j}`, lerp(px(i), px(i + 1), (level - tl) / (tr - tl)), pz(j)];
    const R: P = [`v${i + 1},${j}`, px(i + 1), lerp(pz(j), pz(j + 1), (level - tr) / (br - tr))];
    const B: P = [`h${i},${j + 1}`, lerp(px(i), px(i + 1), (level - bl) / (br - bl)), pz(j + 1)];
    const L: P = [`v${i},${j}`, px(i), lerp(pz(j), pz(j + 1), (level - tl) / (bl - tl))];
    const mid = (tl + tr + br + bl) / 4 >= level;
    switch (code) {
      case 1: case 14: segs.push([L, B]); break;
      case 2: case 13: segs.push([B, R]); break;
      case 3: case 12: segs.push([L, R]); break;
      case 4: case 11: segs.push([T, R]); break;
      case 6: case 9: segs.push([T, B]); break;
      case 7: case 8: segs.push([T, L]); break;
      case 5: if (mid) segs.push([T, L], [R, B]); else segs.push([T, R], [L, B]); break;
      case 10: if (mid) segs.push([T, R], [L, B]); else segs.push([T, L], [R, B]); break;
    }
  }
  const byKey = new Map<string, number[]>();
  segs.forEach((s, k) => { for (const p of s) { let l = byKey.get(p[0]); if (!l) byKey.set(p[0], (l = [])); l.push(k); } });
  const used = new Uint8Array(segs.length);
  const loops: [number, number][][] = [];
  for (let k = 0; k < segs.length; k++) {
    if (used[k]) continue;
    used[k] = 1;
    const loop: [number, number][] = [[segs[k]![0][1], segs[k]![0][2]]];
    let pt = segs[k]![1];
    for (let guard = 0; guard < 400_000; guard++) {
      loop.push([pt[1], pt[2]]);
      const next = (byKey.get(pt[0]) ?? []).find((m) => !used[m]);
      if (next === undefined) break;
      used[next] = 1;
      pt = segs[next]![0][0] === pt[0] ? segs[next]![1] : segs[next]![0];
    }
    if (loop.length >= 4) loops.push(loop);
  }
  const chaikin = (pts: [number, number][]) => {
    for (let r = 0; r < 2; r++) {
      const out: [number, number][] = [];
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i]!, q = pts[(i + 1) % pts.length]!;
        out.push([p[0] * 0.75 + q[0] * 0.25, p[1] * 0.75 + q[1] * 0.25], [p[0] * 0.25 + q[0] * 0.75, p[1] * 0.25 + q[1] * 0.75]);
      }
      pts = out;
    }
    return pts;
  };
  const area = (p: [number, number][]) => { let a = 0; for (let i = 0; i < p.length; i++) { const q = p[(i + 1) % p.length]!; a += p[i]![0] * q[1] - q[0] * p[i]![1]; } return a / 2; };
  const inside = (pt: [number, number], poly: [number, number][]) => {
    let c = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, zi] = poly[i]!, [xj, zj] = poly[j]!;
      if (zi > pt[1] !== zj > pt[1] && pt[0] < ((xj - xi) * (pt[1] - zi)) / (zj - zi) + xi) c = !c;
    }
    return c;
  };
  const smoothed = loops.map((l) => simplifyLoop(chaikin(l.filter((_, i) => i % 2 === 0)), CONTOUR_EPS, CONTOUR_RUN)).filter((l) => Math.abs(area(l)) > 1)
    .sort((a, c) => Math.abs(area(c)) - Math.abs(area(a)));
  const depth = smoothed.map((l, i) => smoothed.filter((o, k) => k !== i && Math.abs(area(o)) > Math.abs(area(l)) && inside(l[0]!, o)).length);
  const toV = (l: [number, number][]) => l.map(([x, z]) => new THREE.Vector2(x, -z));
  const shapes: THREE.Shape[] = [];
  smoothed.forEach((l, i) => {
    if (depth[i]! % 2) return;
    const outer = toV(l);
    const shape = new THREE.Shape(THREE.ShapeUtils.isClockWise(outer) ? outer.reverse() : outer);
    smoothed.forEach((h, k) => {
      if (depth[k] === depth[i]! + 1 && inside(h[0]!, l)) {
        const hole = toV(h);
        shape.holes.push(new THREE.Path(THREE.ShapeUtils.isClockWise(hole) ? hole : hole.reverse()));
      }
    });
    shapes.push(shape);
  });
  return shapes;
}

/** Surface of a river or pond tile at `tier` (the water a quarter tile below its banks). */
export const waterSurface = (tier: number): number => tier * tuning.map.tierRise - 0.25;
/** Tiles the sand heightfield never covers: land of the main level and above, and the rivers and ponds cut into it. */
export const aboveBeach = (map: Tiles, x: number, y: number): boolean => {
  const g = groundAt(map, x, y);
  return g !== 'sand' && g !== 'sea' && tierAt(map, x, y) >= 1;
};

/** Sand slope and sea shelf (DESIGN.md "Beach"): 0 at the main level's foot, ~−0.25 at the waterline, shelf −0.52, seabed −1.55.
 *  Rivers crossing the beach at tier 0 are cut in as a channel below their water. */
export function beachHeight(map: Tiles): (x: number, z: number) => number {
  const { w, h } = map;
  const dist = new Int32Array(w * h).fill(-1), q: number[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (groundAt(map, x, y) !== 'sea' && groundAt(map, x, y) !== 'sand') { dist[y * w + x] = 0; q.push(y * w + x); }
  for (let head = 0; head < q.length; head++) {
    const i = q[head]!, x = i % w, y = (i - x) / w;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h || dist[ny * w + nx] !== -1) continue;
      dist[ny * w + nx] = dist[i]! + 1; q.push(ny * w + nx);
    }
  }
  const tileH = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const g = groundAt(map, x, y), d = dist[y * w + x]!;
    let v = 0;                                                                          // land of every tier: the main level's foot
    if (g === 'sand') v = Math.max(-0.27, -0.05 * d);                                   // a wide beach levels off just above the sea
    else if (g === 'sea') {
      let sandNear = false;
      for (let k = 1; k <= 3 && !sandNear; k++) sandNear = [[k, 0], [-k, 0], [0, k], [0, -k]].some(([a, c]) => groundAt(map, x + a!, y + c!) === 'sand');
      v = sandNear ? -0.52 : -1.55;
    } else if ((g === 'river' || g === 'pond') && tierAt(map, x, y) === 0) v = -0.7;  // the channel under a river on the beach
    tileH[y * w + x] = v;
  }
  const at = (x: number, y: number) => tileH[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))]!;
  return (x, z) => {
    const fx = x - 0.5, fz = z - 0.5, ix = Math.floor(fx), iz = Math.floor(fz), tx = fx - ix, tz = fz - iz;
    return at(ix, iz) * (1 - tx) * (1 - tz) + at(ix + 1, iz) * tx * (1 - tz) + at(ix, iz + 1) * (1 - tx) * tz + at(ix + 1, iz + 1) * tx * tz;
  };
}
